import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Legacy Google OAuth session_id flow (from the old Emergent redirect).
      // Harmless if not present; kept so any in-flight tab still resolves.
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        window.history.replaceState(null, '', window.location.pathname);
        await processGoogleSession(sessionId);
        return;
      }

      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
        await fetchUser(storedToken);
      } else {
        await checkExistingSession();
      }
    };

    initAuth();
  }, []);

  const checkExistingSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/check-session`, {
        withCredentials: true,
      });
      if (response.data.authenticated) {
        setUser(response.data.user);
        setToken(response.data.access_token);
        localStorage.setItem('token', response.data.access_token);
      }
    } catch {
      // No existing session — fall through to logged-out state
    } finally {
      setLoading(false);
    }
  };

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/google/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      if (response.data.success) {
        setUser(response.data.user);
        setToken(response.data.access_token);
        localStorage.setItem('token', response.data.access_token);
        window.location.href = '/dashboard';
      }
    } catch {
      // Silently fail back to logged-out
      setLoading(false);
    }
  };

  const fetchUser = async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken || token}` },
      });
      setUser(response.data);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setToken(response.data.access_token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.access_token);
    return response.data;
  };

  const signup = async (data) => {
    const response = await axios.post(`${API}/auth/signup`, data);
    setToken(response.data.access_token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.access_token);
    return response.data;
  };

  /**
   * Sign in / sign up with Google. Accepts either:
   *   - { credential }   — an ID token (JWT), from <GoogleLogin />'s credential
   *   - { access_token } — an OAuth access token, from useGoogleLogin's implicit flow
   *
   * Backend verifies whichever was given and returns our standard
   * { access_token, user } response.
   */
  const loginWithGoogle = async (payload) => {
    const body =
      typeof payload === 'string'
        ? { credential: payload }
        : payload || {};
    const response = await axios.post(`${API}/auth/google/verify`, body);
    setToken(response.data.access_token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.access_token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Even if the server call fails, we still clear local state below
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const sendOTP = async (phone) => {
    const response = await axios.post(`${API}/auth/send-otp`, { phone });
    return response.data;
  };

  const verifyOTP = async (phone, otp) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { phone, otp });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, loginWithGoogle, logout, sendOTP, verifyOTP }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};